export const LAW_CATEGORIES = [
  'Constitutional Law',
  'Criminal Law',
  'Civil Law',
  'Company Law',
  'Contract Law',
  'Taxation',
  'GST',
  'Income Tax',
  'Intellectual Property',
  'Labour and Employment',
  'Environmental Law',
  'Family Law',
  'Arbitration',
  'Insolvency and Bankruptcy',
  'Real Estate and RERA',
  'Banking and Finance',
  'Competition Law',
];

export const COURTS = [
  'Supreme Court of India',
  'Delhi High Court',
  'Bombay High Court',
  'Calcutta High Court',
  'Madras High Court',
  'Karnataka High Court',
  'Allahabad High Court',
  'Gujarat High Court',
  'Rajasthan High Court',
  'Punjab and Haryana High Court',
  'National Company Law Appellate Tribunal',
  'National Company Law Tribunal',
  'Income Tax Appellate Tribunal',
];

export const SECTIONS = [
  {
    id: 'basic',
    label: 'Basic Information',
    description: 'Title, citation, court, and the narrative that introduces the case.',
  },
  {
    id: 'media',
    label: 'Images and SEO',
    description: 'Upload one featured image. The public page builds its address and search description from the title and short summary.',
  },
  {
    id: 'facts',
    label: 'Facts of the Case',
    description: 'Parties, the dispute, and a chronological record of events.',
  },
  {
    id: 'issues',
    label: 'Legal Issues',
    description: 'Questions the court was asked to decide.',
  },
  {
    id: 'provisions',
    label: 'Legal Provisions',
    description: 'Constitutional provisions, statutes, and rules considered in the case.',
  },
  {
    id: 'arguments',
    label: 'Arguments of the Parties',
    description: 'Submissions of the petitioner or appellant and the respondent.',
  },
  {
    id: 'judgment',
    label: 'Judgment / Decision',
    description: 'The holding, reasoning, precedents, and impact of the decision.',
  },
  {
    id: 'related',
    label: 'Related Case Laws',
    description: 'Other decisions and references a reader should see.',
  },
];

export const PROVISION_GROUPS = [
  { type: 'constitutional', label: 'Constitutional Provisions', placeholder: 'e.g. Article 14 of the Constitution of India' },
  { type: 'statutory', label: 'Statutory Provisions', placeholder: 'e.g. Section 9 of the Arbitration and Conciliation Act, 1996' },
  { type: 'rules', label: 'Rules and Regulations', placeholder: 'e.g. Order XXXIX Rule 1 of the Code of Civil Procedure' },
];

const BASIC_FIELDS = ['title', 'case_name', 'citation', 'court', 'year_of_decision', 'decision_date', 'category', 'short_summary', 'introduction', 'brief_background', 'importance'];
const MEDIA_FIELDS = ['featured_image', 'image_alt', 'meta_title', 'meta_description', 'slug'];
const FACT_FIELDS = ['relevant_facts', 'parties_involved', 'events_leading'];
const ARGUMENT_FIELDS = ['petitioner_arguments', 'respondent_arguments'];
const JUDGMENT_FIELDS = ['final_decision', 'relief_granted', 'ratio_decidendi', 'judicial_reasoning', 'interpretation_of_law', 'important_reasoning', 'precedents', 'impact', 'conclusion'];

export function emptyFact() {
  return { event_date: '', description: '' };
}

export function emptyIssue() {
  return { issue_text: '' };
}

export function emptyProvision() {
  return { provision_text: '' };
}

export function emptyRelatedCase() {
  return { case_name: '', citation: '', court: '', year: '', reference_url: '' };
}

export function emptyForm() {
  return {
    id: null,
    title: '',
    case_name: '',
    citation: '',
    court: '',
    year_of_decision: '',
    decision_date: '',
    category: '',
    short_summary: '',
    introduction: '',
    brief_background: '',
    importance: '',
    featured_image: '',
    featured_image_url: '',
    image_alt: '',
    meta_title: '',
    meta_description: '',
    slug: '',
    relevant_facts: '',
    parties_involved: '',
    events_leading: '',
    petitioner_arguments: '',
    respondent_arguments: '',
    final_decision: '',
    relief_granted: '',
    ratio_decidendi: '',
    judicial_reasoning: '',
    interpretation_of_law: '',
    important_reasoning: '',
    precedents: '',
    impact: '',
    conclusion: '',
    show_read_count: false,
    read_count: 0,
    facts: [emptyFact()],
    legal_issues: [emptyIssue()],
    provisions: {
      constitutional: [emptyProvision()],
      statutory: [emptyProvision()],
      rules: [emptyProvision()],
    },
    related_cases: [emptyRelatedCase()],
  };
}

export function isReadCountVisible(value) {
  return value === true || value === 1 || value === '1';
}

export function toSlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 200);
}

function text(value) {
  return value == null ? '' : String(value);
}

export function formFromBlog(blog) {
  const form = emptyForm();
  form.id = blog.id;
  BASIC_FIELDS.concat(MEDIA_FIELDS, FACT_FIELDS, ARGUMENT_FIELDS, JUDGMENT_FIELDS).forEach((key) => {
    if (key === 'featured_image') {
      form.featured_image = text(blog.featured_image);
      return;
    }
    form[key] = text(blog[key]);
  });
  form.featured_image_url = text(blog.featured_image_url);
  form.show_read_count = isReadCountVisible(blog.show_read_count);
  form.read_count = Number(blog.read_count) || 0;
  form.year_of_decision = blog.year_of_decision ? String(blog.year_of_decision) : '';
  form.decision_date = text(blog.decision_date).slice(0, 10);

  const facts = Array.isArray(blog.facts) ? blog.facts : [];
  form.facts = facts.length
    ? facts.map((fact) => ({
        event_date: text(fact.event_date).slice(0, 10),
        description: text(fact.description),
      }))
    : [emptyFact()];

  const issues = Array.isArray(blog.legal_issues) ? blog.legal_issues : [];
  form.legal_issues = issues.length
    ? issues.map((issue) => ({ issue_text: text(issue.issue_text) }))
    : [emptyIssue()];

  const grouped = {
    constitutional: [],
    statutory: [],
    rules: [],
  };
  (Array.isArray(blog.provisions) ? blog.provisions : []).forEach((item) => {
    if (grouped[item.provision_type]) {
      grouped[item.provision_type].push({ provision_text: text(item.provision_text) });
    }
  });
  Object.keys(grouped).forEach((type) => {
    if (grouped[type].length === 0) grouped[type].push(emptyProvision());
  });
  form.provisions = grouped;

  const related = Array.isArray(blog.related_cases) ? blog.related_cases : [];
  form.related_cases = related.length
    ? related.map((item) => ({
        case_name: text(item.case_name),
        citation: text(item.citation),
        court: text(item.court),
        year: item.year ? String(item.year) : '',
        reference_url: text(item.reference_url),
      }))
    : [emptyRelatedCase()];

  return form;
}

export function toPayload(form, status) {
  const facts = form.facts
    .filter((item) => item.event_date || item.description.trim())
    .map((item) => ({
      event_date: item.event_date || '',
      description: item.description.trim(),
    }));

  const legalIssues = form.legal_issues
    .map((item) => item.issue_text.trim())
    .filter(Boolean)
    .map((issue_text) => ({ issue_text }));

  const provisions = PROVISION_GROUPS.flatMap(({ type }) =>
    form.provisions[type]
      .map((item) => item.provision_text.trim())
      .filter(Boolean)
      .map((provision_text) => ({ provision_type: type, provision_text }))
  );

  const relatedCases = form.related_cases
    .filter((item) => item.case_name.trim() || item.citation.trim() || item.court.trim() || item.year || item.reference_url.trim())
    .map((item) => ({
      case_name: item.case_name.trim(),
      citation: item.citation.trim(),
      court: item.court.trim(),
      year: item.year || '',
      reference_url: item.reference_url.trim(),
    }));

  return {
    id: form.id || null,
    title: form.title.trim(),
    case_name: form.case_name.trim(),
    citation: form.citation.trim(),
    court: form.court.trim(),
    year_of_decision: form.year_of_decision || '',
    decision_date: form.decision_date || '',
    category: form.category.trim(),
    short_summary: form.short_summary.trim(),
    introduction: form.introduction.trim(),
    brief_background: form.brief_background.trim(),
    importance: form.importance.trim(),
    featured_image: form.featured_image || '',
    image_alt: form.image_alt.trim() || form.title.trim(),
    meta_title: form.meta_title.trim() || form.title.trim(),
    meta_description: form.meta_description.trim() || form.short_summary.trim(),
    slug: form.slug.trim() || toSlug(form.title),
    relevant_facts: form.relevant_facts.trim(),
    parties_involved: form.parties_involved.trim(),
    events_leading: form.events_leading.trim(),
    petitioner_arguments: form.petitioner_arguments.trim(),
    respondent_arguments: form.respondent_arguments.trim(),
    final_decision: form.final_decision.trim(),
    relief_granted: form.relief_granted.trim(),
    ratio_decidendi: form.ratio_decidendi.trim(),
    judicial_reasoning: form.judicial_reasoning.trim(),
    interpretation_of_law: form.interpretation_of_law.trim(),
    important_reasoning: form.important_reasoning.trim(),
    precedents: form.precedents.trim(),
    impact: form.impact.trim(),
    conclusion: form.conclusion.trim(),
    show_read_count: form.show_read_count ? 1 : 0,
    status,
    facts,
    legal_issues: legalIssues,
    provisions,
    related_cases: relatedCases,
  };
}

export function toPreviewModel(form, status) {
  return {
    ...toPayload(form, status || 'draft'),
    featured_image_url: form.featured_image_url || '',
    status: status || 'draft',
  };
}

function requireText(errors, value, key, message) {
  if (!String(value || '').trim()) {
    errors[key] = message;
  }
}

export function validateBlog(form) {
  const errors = {};
  if (!form.title.trim()) {
    errors.title = 'Blog title is required.';
  } else if (form.title.trim().length > 500) {
    errors.title = 'Title must be 500 characters or fewer.';
  }

  requireText(errors, form.case_name, 'case_name', 'Case name is required.');
  requireText(errors, form.citation, 'citation', 'Citation is required.');
  requireText(errors, form.court, 'court', 'Court is required.');
  requireText(errors, form.category, 'category', 'Category is required.');
  requireText(errors, form.short_summary, 'short_summary', 'Short summary is required.');
  requireText(errors, form.introduction, 'introduction', 'Introduction is required.');
  requireText(errors, form.relevant_facts, 'relevant_facts', 'Relevant facts are required.');
  requireText(errors, form.parties_involved, 'parties_involved', 'Parties involved are required.');
  requireText(errors, form.events_leading, 'events_leading', 'Events leading to the dispute are required.');
  requireText(errors, form.final_decision, 'final_decision', 'Final decision of the court is required.');
  requireText(errors, form.relief_granted, 'relief_granted', 'Relief granted or denied is required.');
  requireText(errors, form.impact, 'impact', 'Impact of the judgment is required.');
  requireText(errors, form.conclusion, 'conclusion', 'Conclusion is required.');

  if (!String(form.year_of_decision || '').trim()) {
    errors.year_of_decision = 'Year of decision is required.';
  } else {
    const year = Number(form.year_of_decision);
    if (!/^\d{4}$/.test(String(form.year_of_decision)) || year < 1800 || year > 2100) {
      errors.year_of_decision = 'Enter a year between 1800 and 2100.';
    }
  }

  if (!form.decision_date) {
    errors.decision_date = 'Decision date is required.';
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(form.decision_date)) {
    errors.decision_date = 'Enter a valid decision date.';
  }

  form.facts.forEach((item, index) => {
    const description = item.description.trim();
    const hasDate = Boolean(item.event_date);
    if (!hasDate && !description) return;
    if (!hasDate) {
      errors[`fact_${index}_event_date`] = 'Event date is required when an event is added.';
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(item.event_date)) {
      errors[`fact_${index}_event_date`] = 'Enter a valid event date.';
    }
    if (!description) {
      errors[`fact_${index}_description`] = 'Event description is required when an event is added.';
    }
  });

  if (!form.legal_issues.some((item) => item.issue_text.trim())) {
    errors.legal_issues = 'Add at least one legal issue.';
    errors.issue_0_issue_text = 'Legal issue is required.';
  }

  if (!form.provisions.statutory.some((item) => item.provision_text.trim())) {
    errors.provisions = 'Add at least one statutory provision.';
    errors.provision_statutory_0 = 'Statutory provision is required.';
  }

  form.related_cases.forEach((item, index) => {
    const url = item.reference_url.trim();
    const anyValue = item.case_name.trim() || item.citation.trim() || item.court.trim() || item.year || url;
    if (!anyValue) return;
    if (!item.case_name.trim()) {
      errors[`related_${index}_case_name`] = 'Case name is required for a related case.';
    }
    if (item.year && (!/^\d{4}$/.test(String(item.year)) || Number(item.year) < 1800 || Number(item.year) > 2100)) {
      errors[`related_${index}_year`] = 'Enter a year between 1800 and 2100.';
    }
    if (url && !/^https?:\/\/.+/i.test(url)) {
      errors[`related_${index}_reference_url`] = 'Enter a valid URL starting with http:// or https://.';
    }
  });

  return errors;
}

export function sectionHasError(sectionId, errors) {
  const keys = Object.keys(errors);
  if (keys.length === 0) return false;
  if (sectionId === 'basic') return keys.some((key) => BASIC_FIELDS.includes(key));
  if (sectionId === 'media') return keys.some((key) => MEDIA_FIELDS.includes(key));
  if (sectionId === 'facts') return keys.some((key) => key === 'facts' || key.startsWith('fact_') || FACT_FIELDS.includes(key));
  if (sectionId === 'issues') return keys.some((key) => key === 'legal_issues' || key.startsWith('issue_'));
  if (sectionId === 'provisions') return keys.some((key) => key === 'provisions' || key.startsWith('provision_'));
  if (sectionId === 'arguments') return keys.some((key) => ARGUMENT_FIELDS.includes(key));
  if (sectionId === 'judgment') return keys.some((key) => JUDGMENT_FIELDS.includes(key));
  if (sectionId === 'related') return keys.some((key) => key === 'related_cases' || key.startsWith('related_'));
  return false;
}

export function firstErrorSection(errors) {
  return SECTIONS.find((section) => sectionHasError(section.id, errors))?.id || 'basic';
}

export function formatTimestamp(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatLongDate(value) {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function moveItem(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}
